// Services
import API from 'src/api'
import { OfflineStorage } from 'src/services/offline-storage'
import { isReallyOnline } from 'src/services/connectivity'
// Offline
import { newId } from './repositories/ids'
import { getTransport } from './sync/transport'
import { is2xx, throwFromResponse } from './sync/http-helpers'

/**
 * pending-signatures.js: respaldo de firmas cuando no se pudieron guardar en SQLite ni enviar (F-33/F-34).
 *
 * Con la base local abierta las firmas van por el outbox (occurrence.saveSignature). Con la base cerrada
 * (plugin de SQLite muerto, ISS-1009) o si esa escritura falla, la firma se manda con el PUT en línea; si
 * ese PUT tampoco sale (sin red o error del servidor), se guarda aquí, en OfflineStorage (JSON en disco /
 * localStorage, independiente de SQLite), igual que el respaldo legacy de los addons. Se reenvía sola al
 * volver la red y cuando la store drena el respaldo (al reconectar y al finalizar).
 *
 * Archivo propio (pending_signatures.json), NO answers.json: las filas de answers.json las leen el drain
 * legacy, el overlay de reingreso, la limpieza de F-18 y la migración v1, y una firma ahí pasaría por
 * respuesta.
 *
 * ENVÍO: PUT occurrences/<occ>/form-assignments/<assignment> con una sola firma, el mismo endpoint del
 * camino en línea (el backend crea cada firma del arreglo). Un solo replay a la vez (single-flight): dos
 * disparos simultáneos mandarían la misma firma dos veces y el backend la duplicaría.
 *
 * Una firma enviada queda como lápida con su id de servidor, para poder borrarla sin recargar.
 */

const GRAPHQL_URL = API.GRAPHQL_URL

const MAX_APPLIED_TOMBSTONES = 25

const isPending = (record) => !!record && !record.applied_at

const errorMessage = (error) => String((error && error.message) || error)

function withDefaults (options) {
  return {
    storage: OfflineStorage,
    transport: getTransport,
    isOnline: isReallyOnline,
    ...options
  }
}

function trimApplied (records) {
  const applied = records.filter(record => !isPending(record))
  const dropped = new Set(applied.slice(0, Math.max(0, applied.length - MAX_APPLIED_TOMBSTONES)))
  return records.filter(record => !dropped.has(record))
}

async function readRecords (storage) {
  const stored = await storage.getPendingSignatures()
  return Array.isArray(stored) ? stored : []
}

// Escrituras serializadas: guardar, quitar y el replay leen y reescriben el mismo archivo.
let writeChain = Promise.resolve()
function serialized (work) {
  const run = writeChain.then(work, work)
  writeChain = run.catch(() => {})
  return run
}

/**
 * Guarda firmas que no se pudieron guardar en SQLite ni enviar.
 * @param {object} args { occurrenceId, formAssignmentId, signatures } (firmas en el formato del PUT)
 * @param {object} [options] { storage }
 * @returns {Promise<string[]>} la referencia de cada firma, en el mismo orden.
 */
export function savePendingSignatures ({ occurrenceId, formAssignmentId, signatures = [] } = {}, options = {}) {
  const { storage } = withDefaults(options)
  return serialized(async () => {
    const records = await readRecords(storage)
    const refs = signatures.map(signature => {
      const ref = `sig-${newId()}`
      records.push({
        ref,
        occurrence_id: String(occurrenceId),
        form_assignment_id: formAssignmentId,
        signature,
        created_at: new Date().toISOString(),
        attempts: 0
      })
      return ref
    })
    await storage.setPendingSignatures(records)
    return refs
  })
}

/**
 * Firmas pendientes de envío de una auditoría (para pintarlas al reabrir).
 * @returns {Promise<Array<{ ref: string, signature: object }>>}
 */
export async function listPendingSignatures (occurrenceId, options = {}) {
  const { storage } = withDefaults(options)
  const records = await readRecords(storage)
  return records
    .filter(record => isPending(record) && record.occurrence_id === String(occurrenceId))
    .map(record => ({ ref: record.ref, signature: record.signature }))
}

/**
 * Quita una firma del respaldo. Si todavía no se envió, se borra sin ir al servidor; si ya se envió,
 * devuelve su id de servidor para que el caller la borre allá.
 * @returns {Promise<{ removed: boolean, serverId: (number|null) }>}
 */
export function removePendingSignature (ref, options = {}) {
  const { storage } = withDefaults(options)
  return serialized(async () => {
    const records = await readRecords(storage)
    const record = records.find(r => r.ref === ref)
    if (!record) return { removed: false, serverId: null }
    if (!isPending(record)) return { removed: false, serverId: record.server_id ?? null }
    await storage.setPendingSignatures(records.filter(r => r !== record))
    return { removed: true, serverId: null }
  })
}

async function sendOne (record, deps) {
  const res = await deps.transport().put(
    `${GRAPHQL_URL}occurrences/${record.occurrence_id}/form-assignments/${record.form_assignment_id}`,
    {
      id: record.occurrence_id,
      form_assignment_id: record.form_assignment_id,
      answers: [],
      signatures: [record.signature]
    },
    { headers: { 'Idempotency-Key': `pending-signature:${record.ref}` } }
  )
  if (!is2xx(res)) throwFromResponse(res)
  const created = res?.data?.data?.signatures
  return Array.isArray(created) && created[0]?.id != null ? created[0].id : null
}

async function runReplay (options) {
  const deps = withDefaults(options)
  const pendingCount = (await readRecords(deps.storage)).filter(isPending).length
  if (!pendingCount) return { sent: [], pending: 0 }
  if (!(await deps.isOnline())) return { sent: [], pending: pendingCount }

  return serialized(async () => {
    const records = await readRecords(deps.storage)
    const sent = []
    const pending = records.filter(isPending)
    for (const record of pending) {
      try {
        record.server_id = await sendOne(record, deps)
      } catch (error) {
        record.attempts = (record.attempts || 0) + 1
        record.last_error = errorMessage(error)
        console.error('[pending-signatures] no se pudo enviar la firma pendiente:', error)
        continue
      }
      record.applied_at = new Date().toISOString()
      sent.push({ ref: record.ref, serverId: record.server_id })
      await deps.storage.setPendingSignatures(records)
    }
    if (pending.length) await deps.storage.setPendingSignatures(trimApplied(records))
    return { sent, pending: records.filter(isPending).length }
  })
}

let inFlightReplay = null

/**
 * Reenvía las firmas pendientes. Idempotente y seguro de llamar varias veces: las llamadas concurrentes
 * comparten la misma pasada. Una firma que falla se queda pendiente para el siguiente intento.
 * @returns {Promise<{ sent: Array<{ ref: string, serverId: (number|null) }>, pending: number }>}
 */
export function replayPendingSignatures (options = {}) {
  if (inFlightReplay) return inFlightReplay
  inFlightReplay = runReplay(options).finally(() => { inFlightReplay = null })
  return inFlightReplay
}

let listenerInstalled = false

export function startPendingSignatureWatcher () {
  if (listenerInstalled) return
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  listenerInstalled = true
  window.addEventListener('online', () => {
    replayPendingSignatures().catch(error => console.error('[pending-signatures] replay fallo:', error))
  })
}

startPendingSignatureWatcher()
